import { useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import List from '@mui/material/List';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Preloader from 'components/Preloader';
import { useTranslation } from 'react-i18next';
import { GroupedVirtuoso } from 'react-virtuoso';
import ProductItemVirtual from 'components/MenuNew/ProductItemVirtual';
import Skeleton from '@mui/material/Skeleton';
import CategoryItem from 'components/MenuNew/CategoryItem';
import Private from 'components/Private';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useHistory } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { SET_PRODUCT_EVENTS_NEXT_LOCATION } from 'components/ConfirmProductActions/constants';
import usePermission from 'hooks/permission';
import * as permissions from 'constants/permissions';
// import SortableProductItem from 'components/MenuNew/SortableProductItem';

export default function NestedList({ searchQuery }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { menu, loading } = useSelector(({ menu }) => menu);
  const [filteredData, setFilteredData] = useState([]);
  const [collapsedByCatId, setCollapsedByCatId] = useState({});
  const history = useHistory();
  const checkPermission = usePermission();
  const canEditMenu = checkPermission(permissions.MENU_EDIT);
  const handleAddNewProduct = useMemo(
    () => handleAddNewProductFactory(history, dispatch),
    [history, dispatch]
  );

  const toggleCollapsed = useCallback((catId) => {
    setCollapsedByCatId((prev) => ({ ...prev, [catId]: !prev[catId] }));
  }, []);

  // const handleSortEnd = useCallback(
  //   ({ oldIndex, newIndex }) => {
  //     if (oldIndex !== newIndex) {
  //       const arr1 = menu.categories.map((item) => item._id);
  //       const arr2 = arrayReRange(arr1, oldIndex, newIndex);
  //       dispatch(reorderCategories(arr2));
  //     }
  //   },
  //   [dispatch, menu]
  // );

  const normalizedCategories = useMemo(() => menu?.categories || [], [menu]);

  const visibleGroups = useMemo(() => {
    const query = (searchQuery || '').toLowerCase();
    if (!query) {
      return normalizedCategories.map((cat) => ({
        cat,
        filtered: cat.products || [],
        include: true,
      }));
    }
    return normalizedCategories
      .map((cat) => {
        const products = cat.products || [];
        const filtered = products.filter((p) =>
          (p?.name || '').toLowerCase().includes(query)
        );
        console.log('filtered', filtered);
        console.log('query', query);
        const catMatch = (cat?.name || '').toLowerCase().includes(query);
        const include = catMatch || filtered.length > 0;
        return { cat, filtered, include };
      })
      .filter((x) => x.include);
  }, [normalizedCategories, searchQuery]);

  const groupCounts = useMemo(() => {
    return visibleGroups.map(({ filtered, cat }) => {
      if (collapsedByCatId[cat._id]) {
        return 0;
      }
      if (searchQuery) {
        const filteredValid = (filtered || []).filter((p) => !!p && !!p._id);
        return filteredValid.length + (canEditMenu ? 1 : 0);
      }
      const items = (cat.products || []).filter((p) => !!p && !!p._id);
      return items.length + (canEditMenu ? 1 : 0);
    });
  }, [visibleGroups, searchQuery, collapsedByCatId, canEditMenu]);

  const groupStartOffsets = useMemo(() => {
    let acc = 0;
    return groupCounts.map((count) => {
      const start = acc;
      acc += count;
      return start;
    });
  }, [groupCounts]);

  // const searchBar = useCallback(
  //   (data) => {
  //     setFilteredData(data);
  //   },
  //   [searchQuery]
  // );

  return (
    <List
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        pt: 0,
        flex: 1,
      }}
    >
      <div
        className="menu-categories-body"
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flex: 1,
        }}
      >
        {loading && <Preloader overlay={true} />}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            flex: 1,
            minHeight: 0,
          }}
          className="menu-categories-list"
        >
          {searchQuery && !filteredData?.length ? (
            <Typography align="center" sx={{ p: 4 }}>
              {t('menu.product_category_results')}
            </Typography>
          ) : (
            <></>
          )}{' '}
          {normalizedCategories?.length ? (
            <GroupedVirtuoso
              style={{ height: '100%', flex: 1, minHeight: 0 }}
              followOutput={false}
              increaseViewportBy={{ top: 300, bottom: 600 }}
              computeItemKey={(index, groupIndex) => {
                const group = visibleGroups[groupIndex];
                if (!group) return `${groupIndex}-${index}`;
                const { cat, filtered } = group;
                const raw = searchQuery
                  ? Array.isArray(filtered)
                    ? filtered
                    : []
                  : Array.isArray(cat?.products)
                  ? cat.products
                  : [];
                const arr = raw.filter((p) => !!p && !!p._id);
                const localIndex = index - (groupStartOffsets[groupIndex] || 0);
                if (canEditMenu && localIndex === arr.length) {
                  return `${cat?._id || 'cat'}-add`;
                }
                const keyItem = arr[localIndex];
                return keyItem?._id || `${cat?._id || 'cat'}-${localIndex}`;
              }}
              groupCounts={groupCounts}
              groupContent={(groupIndex) => {
                const { cat } = visibleGroups[groupIndex];
                const isCollapsed = !!collapsedByCatId[cat._id];
                return (
                  <CategoryItem
                    category={cat}
                    menu={menu}
                    indexCat={groupIndex}
                    searchQuery={searchQuery}
                    setFilteredData={setFilteredData}
                    isCollapsed={isCollapsed}
                    onToggle={() => toggleCollapsed(cat._id)}
                  />
                );
              }}
              itemContent={(index, groupIndex) => {
                const { cat, filtered } = visibleGroups[groupIndex];
                const items = (
                  searchQuery ? filtered : cat.products || []
                ).filter((p) => !!p && !!p._id);
                const localIndex = index - (groupStartOffsets[groupIndex] || 0);
                const isAddRow = canEditMenu && localIndex === items.length;
                if (isAddRow) {
                  return (
                    <Private permission="MENU_EDIT">
                      <ListItemButton id="sortableElementAdd" sx={{ px: 8 }}>
                        <ListItemText
                          onClick={() => handleAddNewProduct(cat._id)}
                          primary={
                            <Typography
                              sx={{ fontSize: '14px', fontWeight: '500' }}
                            >
                              + {t('menu.add_item')}
                            </Typography>
                          }
                        />
                      </ListItemButton>
                    </Private>
                  );
                }
                const product = items[localIndex];
                if (!product) {
                  // Render a stable-height skeleton while products load
                  return (
                    <Box sx={{ px: 2, py: 0.5, ml: 3 }}>
                      <Skeleton variant="rectangular" height={80} />
                    </Box>
                  );
                }
                return (
                  <ProductItemVirtual
                    key={product?._id}
                    menu={menu}
                    cid={cat._id}
                    product={product}
                    category={cat}
                  />
                );
              }}
            />
          ) : (
            <Typography sx={{ p: 4 }} align="center">
              {t('menu.there_is_categories')}
            </Typography>
          )}
        </Box>
      </div>
    </List>
  );
}

function handleAddNewProductFactory(history, dispatch) {
  return (cid) => {
    history.push({
      search: `?entityType=item&pid=${uuidv4()}&cid=${cid}&mode=create`,
    });
    dispatch({
      type: SET_PRODUCT_EVENTS_NEXT_LOCATION,
      payload: `/menu/overview`,
    });
  };
}
